import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import '../../App.css';
import Button from '../../components/ui/Button';
import Modal from '../../components/Modal';
import MemberForm, { IMemberFormData } from '../../components/forms/MemberForm';
import PageHeader from '../../components/PageHeader';
import { ConfirmModal } from '../../components/ui';
import { translateError } from '../../utils/errorMessages';

interface IGroupDisplay {
  _id: string;
  name: string;
}

interface IMember {
  _id: string;
  name: string;
  description: string;
  groups?: IGroupDisplay[];
}

const MyMembersPage: React.FC = () => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<IMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  // A half-typed child's details must not vanish on a stray Escape.
  const [formDirty, setFormDirty] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState<Map<string, { rate: number; total: number; attended: number }>>(new Map());

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const [membersRes, attendanceRes] = await Promise.all([
        api.my.members(),
        api.my.attendance().catch(() => ({ data: { members: [] } })),
      ]);
      setMembers(membersRes.data as IMember[]);

      // Build attendance stats map
      const statsMap = new Map<string, { rate: number; total: number; attended: number }>();
      const attendanceData = (attendanceRes.data as any)?.members || [];
      for (const s of attendanceData) {
        statsMap.set(s.memberId, {
          rate: s.attendanceRate,
          total: s.totalEvents,
          attended: s.attended,
        });
      }
      setAttendanceStats(statsMap);

      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setFormDirty(false);
    setDiscardConfirmOpen(false);
  };

  const guardAddClose = (): boolean | void => {
    if (!formDirty) return;
    setDiscardConfirmOpen(true);
    return false;
  };

  const handleAddMember = async (data: IMemberFormData) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.my.createMember(data);
      closeAddModal();
      fetchMembers();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && members.length === 0) return <p>{t('userDashboard.loadingMembers')}</p>;
  if (error && !showAddModal) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <PageHeader
        title={t('userDashboard.myMembers')}
        helpKey="help.user.members"
        action={
          <Button onClick={() => setShowAddModal(true)}>
            {t('members.createNew')}
          </Button>
        }
      />

      {members.length === 0 ? (
        <p>{t('userDashboard.noMembers')}</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.description')}</th>
              <th>{t('sidebar.groups')}</th>
              <th>{t('attendanceReport.attendanceColumn')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member._id}>
                <td><Link to={`/dashboard/members/${member._id}`}>{member.name}</Link></td>
                <td>{member.description}</td>
                <td>
                  {member.groups && member.groups.length > 0
                    ? member.groups.map((group) => group.name).join(', ')
                    : t('common.noResults')}
                </td>
                <td>
                  {(() => {
                    const stats = attendanceStats.get(member._id);
                    if (!stats || stats.total === 0) return t('attendanceReport.noEvents');
                    return t('attendanceReport.attendanceSummary', {
                      attended: stats.attended,
                      total: stats.total,
                      rate: stats.rate,
                    });
                  })()}
                </td>
                <td>
                  <Link
                    to={`/dashboard/members/${member._id}`}
                    className="btn btn--primary btn--sm"
                    aria-label={`${t('common.edit')} ${member.name}`}
                  >
                    <i className="fas fa-edit" aria-hidden="true"></i>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        onRequestClose={guardAddClose}
        title={t('members.createTitle')}
      >
        {error && <p className="error-message">{error}</p>}
        <MemberForm
          onSubmit={handleAddMember}
          onDirtyChange={setFormDirty}
          loading={submitting}
          submitButtonText={t('members.createButton')}
          isUserView={true}
        />
      </Modal>

      <ConfirmModal
        isOpen={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={closeAddModal}
        title={t('unsavedChanges.title')}
        message={t('unsavedChanges.message')}
        confirmText={t('unsavedChanges.discard')}
        cancelText={t('unsavedChanges.keepEditing')}
        variant="danger"
      />
    </div>
  );
};

export default MyMembersPage;
